/**
 * The search query behind the palette and the `[[` menu (`070-search-and-links.md` §4).
 *
 * Three behaviours that are the difference between a palette that feels instant and one
 * that feels broken, all of them bought from TanStack Query rather than hand-rolled:
 *
 *  1. **Cancellation.** Every keystroke aborts the in-flight request. TanStack hands the
 *     `queryFn` an `AbortSignal` and aborts it when the key changes, so a slow response for
 *     `ith` can never land on top of a fast one for `ithkar`. Pass the signal through; a
 *     `queryFn` that ignores it silently reintroduces the race.
 *  2. **`placeholderData: keepPreviousData`.** The list NEVER blanks between keystrokes —
 *     the previous results stay and are dimmed. Flicker on every character is the classic
 *     palette bug (`070` §4, "Loading").
 *  3. **The debounce lives on the KEY** (`useDebouncedValue`), not around the fetch, so
 *     there is exactly one cache entry per settled query rather than one per character.
 */
import { keepPreviousData, useQuery, type UseQueryResult } from '@tanstack/react-query';
import { keys } from '@/api/keys';
import { search, type SearchResponse } from '@/api/endpoints/search';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';

/** `070` §4: the palette. The `[[` menu asks for 8 (§7.1). */
export const PALETTE_LIMIT = 20;
export const SUGGESTION_LIMIT = 8;
const DEBOUNCE_MS = 150;

export function useSearch(
  query: string,
  options: { enabled?: boolean; limit?: number } = {},
): UseQueryResult<SearchResponse> {
  const { enabled = true, limit = PALETTE_LIMIT } = options;

  // The empty query is not debounced: it is not a request at all (the palette shows recent
  // pages from the tree cache instead), and making the box feel sticky on Backspace would
  // be a debounce doing the opposite of its job.
  const q = useDebouncedValue(query, DEBOUNCE_MS, query === '');
  const active = enabled && q !== '';

  return useQuery({
    queryKey: keys.search(q),
    queryFn: ({ signal }) => search(q, limit, signal),
    enabled: active,
    // Keep the last results on screen while the next ones are in flight. Without this the
    // list empties and re-fills on every character.
    placeholderData: keepPreviousData,
    // `111` §3.2: search results are throwaway, and the key is unbounded — one entry per
    // keystroke would otherwise pile up in the cache all session.
    staleTime: 30_000,
    gcTime: 2 * 60_000,
  });
}
